import { Button, Form, FormCard, PageHeader, Spinner } from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { showToast } from 'common/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { capitalize } from 'lodash'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import useMixpanelEvent, {
  MixpanelEvents
} from '../../../hooks/useMixpanelEvent'
import { SHIFT, VISIT } from '../constants'
import useInstancesDelete from '../hooks/useInstancesDelete'
import useInstancesUpdate from '../hooks/useInstancesUpdate'
import { useRotaPatternDelete } from '../hooks/useRotaPatternDelete'
import { useRotaPatternDetail } from '../hooks/useRotaPatternDetail'
import { useRotaPatternUpdate } from '../hooks/useRotaPatternUpdate'
import RegenerateEventsModal, {
  RegenerateOptions
} from './components/RegenerateEventsModal'
import {
  AboutShiftSection,
  AboutVisitSection,
  AllocationSection,
  DateTimeWindowSection,
  FrequencyResultSection,
  NeedsAssignedSection,
  ReccurencePatternSection,
  ServiceUserDetailsSection,
  SkillsRequiredSection
} from './sections'
import {
  getPatternFormInitialValues,
  type ModalSectionProps,
  type RotaPatternModalType
} from './utils'

type RotaPatternEditFormProps = {
  type: RotaPatternModalType
} & ModalSectionProps

export const RotaPatternEditForm: FC<RotaPatternEditFormProps> = ({ type }) => {
  const [form] = AntdForm.useForm()

  const { t } = useTranslation()

  const { id } = useParams()

  const navigate = useNavigate()

  const sendMixpanelEvent = useMixpanelEvent()

  const { patternDetail, isLoadingDetail } = useRotaPatternDetail({ id })

  const { updatePattern, isLoadingUpdate } = useRotaPatternUpdate({
    id,
    type,
    onUpdateSuccess: () => {
      updateEvents?.({
        patternId: id,
        updateOption: updateEventsOption
      })

      sendMixpanelEvent(MixpanelEvents.ROTA_PATTERN_EDIT_UPDATE_EVENTS, {
        patternId: id,
        patternType: type,
        updateOption: updateEventsOption,
        occurrences: patternDetail?.occurrences
      })

      navigate(-1)
    }
  })

  const { deletePattern, isLoadingDelete } = useRotaPatternDelete({
    id,
    onDeleteSuccess: () => {
      showToast({ title: 'Pattern deleted successfully' })
      deleteEvents?.({
        patternId: id,
        updateOption: updateEventsOption
      })
      navigate(`/rota/patterns?tabKey=${type}s`)
    }
  })

  const { updateEvents } = useInstancesUpdate()

  const { deleteEvents } = useInstancesDelete()

  const initialValues = getPatternFormInitialValues(patternDetail)

  const [formData, setFormData] = useState<Record<string, any>>(initialValues)

  const [updateEventsOption, setUpdateEventsOption] = useState<string>(
    RegenerateOptions.DO_NOT_UPDATE
  )

  const handleFormValuesChange = (values: unknown) => {
    dataChangeHandler(values)
    // @ts-ignore
    setFormData((prev) => ({ ...prev, ...values }))
  }

  const dataChangeHandler = (data: unknown) => {
    if (data) {
      setIsDirty(true)
    }

    if (!hasFormDataTouched) {
      setHasFormDataTouched(true)
    }
  }

  useEffect(() => {
    // We need to keep this because of frequency pattern, after initialization we have to reset dirty check
    setIsDirty(false)
  }, [])

  const handleOnUpdateFinish = async (values: Record<string, any>) => {
    await form.validateFields()
    setFormData((prev) => ({ ...prev, ...values }))

    if (hasFormDataTouched) {
      setShowPrompt(true)
      return
    }

    setIsDirty(false)
    updatePattern(formData)
  }
  const handleDeleted = () => {
    setShowPrompt(true)
    setPromptForDelete(true)
  }

  // Confirm Modal
  const [hasFormDataTouched, setHasFormDataTouched] = useState(false)

  const [showPrompt, setShowPrompt] = useState(false)

  const [promptForDelete, setPromptForDelete] = useState(false)

  // Cancel Modal
  const [isDirty, setIsDirty] = useState(false)

  if (isLoadingDetail) {
    return (
      <LoadingWrapper>
        <Spinner />
      </LoadingWrapper>
    )
  }

  const confirmNavigation = () => {
    setShowPrompt(false)
    if (promptForDelete) {
      deletePattern()
      setPromptForDelete(false)
    } else {
      setHasFormDataTouched(false)
      form.submit()
    }
  }

  return (
    <>
      <PageHeader title={t('EDIT_THE_SHIFT', { variable: capitalize(type) })}>
        <ButtonsWrapper>
          <Button variant="default" onClick={() => navigate(-1)}>
            {t('CANCEL')}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleted}
            isLoading={isLoadingDelete}
          >
            {t('DELETE')}
          </Button>
          <Button
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isLoadingUpdate}
          >
            {t('SAVE')}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <Form
        form={form}
        onFinish={handleOnUpdateFinish}
        onValuesChange={handleFormValuesChange}
        initialValues={formData}
      >
        <FormCard title={t('ABOUT')}>
          {type === VISIT && (
            <>
              <ServiceUserDetailsSection
                form={form}
                formData={formData}
                disableResident
              />
              <AboutVisitSection />
            </>
          )}
          {type === SHIFT && <AboutShiftSection />}
        </FormCard>
        <FormCard title={t('ASSIGN_TEAM_MEMBER')}>
          <AllocationSection formData={formData} />
        </FormCard>
        <FormCard title={t('SKILLS_AND_TAGS')}>
          {type === VISIT && <NeedsAssignedSection formData={formData} />}
          <SkillsRequiredSection type={type} formData={formData} />
        </FormCard>
        <FormCard title={t('RECURRENCE_PATTERN')}>
          <DateTimeWindowSection
            formData={formData}
            setFormData={setFormData}
            form={form}
          />
          <ReccurencePatternSection
            formData={formData}
            setFormData={(e) => {
              setFormData(e)
              dataChangeHandler(e)
            }}
          />
          <FrequencyResultSection form={form} type={type} formData={formData} />
        </FormCard>
      </Form>
      <RegenerateEventsModal
        showPrompt={showPrompt}
        setShowPrompt={setShowPrompt}
        confirmNavigation={confirmNavigation}
        type={type}
        occurrences={patternDetail?.occurrences}
        regenerateMode={updateEventsOption}
        setRegenerateMode={setUpdateEventsOption}
      />
      <NavigationModal canNavigate={!isDirty} />
    </>
  )
}

const ButtonsWrapper = styled.div`
  display: flex;
  margin-left: auto;
  gap: 5px;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`
